import { useState } from 'react'
import type { GenerateScenariosResponse, Scenario } from '../types'
import type React from 'react'

function getTagLabel(tag?: string) {
  if (!tag) return null
  const labels: Record<string, string> = {
    baseline: 'Most Likely',
    tail_risk: 'Tail Risk',
    marginal_yes: 'Close Call (Yes)',
    marginal_no: 'Close Call (No)',
  }
  return labels[tag] || tag
}

function getTagStyle(tag?: string): React.CSSProperties {
  if (!tag) return {}
  const styles: Record<string, React.CSSProperties> = {
    baseline: { backgroundColor: 'var(--green)', color: 'white' },
    tail_risk: { backgroundColor: 'var(--clay-red)', color: 'white' },
    marginal_yes: { backgroundColor: '#f59e0b', color: 'white' },
    marginal_no: { backgroundColor: '#f59e0b', color: 'white' },
  }
  return styles[tag] || {}
}

function ScenariosDisplay({ scenarios }: { scenarios: Scenario[] }) {
  const [selectedScenarioIndex, setSelectedScenarioIndex] = useState(0)

  if (scenarios.length === 0) return null

  return (
    <div className="mt-3">
      <div className="font-semibold text-ink mb-3">How it might play out</div>
      <div className="text-sm text-muted mb-3">
        Illustrative scenarios showing how this forecast outcome might (or might not) unfold.
      </div>

      {/* Scenario selector tabs */}
      <div className="flex gap-2 flex-wrap">
        {scenarios.map((scenario, index) => {
          const isActive = index === selectedScenarioIndex
          const baseColor = scenario.outcome_occurs
            ? isActive ? 'border-green/50 bg-green/15' : 'border-green/30 bg-green/5'
            : isActive ? 'border-clay-red/60 bg-clay-red/20' : 'border-clay-red/40 bg-clay-red/10'
          const borderWeight = scenario.outcome_occurs ? 'border' : 'border-2'

          return (
            <button
              key={index}
              type="button"
              className={`rounded-delphi ${borderWeight} px-3 py-2 text-left transition hover:brightness-95 ${baseColor}`}
              onClick={() => setSelectedScenarioIndex(index)}
            >
              <div className="text-[10px] text-muted font-semibold tracking-wider">{index + 1}</div>
              <div className="text-sm font-semibold text-ink mt-0.5 leading-tight">{scenario.title}</div>
              {scenario.tag && (
                <div
                  className="mt-1 text-[10px] font-semibold tracking-wider rounded px-1.5 py-0.5 inline-block"
                  style={getTagStyle(scenario.tag)}
                >
                  {getTagLabel(scenario.tag)}
                </div>
              )}
            </button>
          )
        })}
      </div>

      {/* Selected scenario content */}
      {scenarios[selectedScenarioIndex] && (
        <div
          className={`mt-3 rounded-delphi border-2 p-4 ${
            scenarios[selectedScenarioIndex].outcome_occurs
              ? 'border-green/40 bg-green/5'
              : 'border-clay-red/40 bg-clay-red/5'
          }`}
        >
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="font-semibold text-lg text-ink">{scenarios[selectedScenarioIndex].title}</div>
              {scenarios[selectedScenarioIndex].tag && (
                <div
                  className="mt-1 text-[10px] font-semibold tracking-wider rounded px-2 py-1 inline-block"
                  style={getTagStyle(scenarios[selectedScenarioIndex].tag)}
                >
                  {getTagLabel(scenarios[selectedScenarioIndex].tag)}
                </div>
              )}
            </div>
            <div
              className={`shrink-0 rounded-full px-3 py-1 text-xs font-semibold ${
                scenarios[selectedScenarioIndex].outcome_occurs
                  ? 'bg-green text-white'
                  : 'text-white'
              }`}
              style={!scenarios[selectedScenarioIndex].outcome_occurs ? { backgroundColor: '#a84848' } : {}}
            >
              {scenarios[selectedScenarioIndex].outcome_occurs ? '✓ Outcome occurs' : '✗ Outcome does not occur'}
            </div>
          </div>

          <p className="mt-3 text-sm text-ink/90 leading-relaxed">{scenarios[selectedScenarioIndex].summary}</p>

          <div className="mt-4">
            <div className="text-xs font-semibold text-muted tracking-wider mb-2">HOW IT UNFOLDS:</div>
            <ul className="space-y-2">
              {scenarios[selectedScenarioIndex].narrative_points.map((point, idx) => (
                <li key={idx} className="text-sm text-ink/90 leading-relaxed pl-4 relative">
                  <span className="absolute left-0 text-muted font-semibold">→</span>
                  {point}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  )
}

export default function ScenariosModal({
  open,
  onClose,
  data,
  error,
  loading,
  onSave,
  saving,
  saved,
}: {
  open: boolean
  onClose: () => void
  data: GenerateScenariosResponse | null
  error?: string | null
  loading?: boolean
  onSave?: () => void
  saving?: boolean
  saved?: boolean
}) {
  if (!open) return null

  const prob = data?.probability ? `${Math.round(data.probability * 100)}%` : '--'

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(ev) => ev.stopPropagation()}>
        <div className="modal-header">
          <h2 className="text-lg sm:text-xl font-semibold text-ink">
            Scenarios for {prob} probability
          </h2>
          <button className="modal-close" type="button" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="modal-body">
          {!loading && data && (
            <div className="flex items-center justify-end gap-2 mb-3">
              <button
                type="button"
                className="rounded-xl px-3 py-2 text-sm font-semibold border border-rule bg-paper shadow-delphi hover:bg-white/30 transition disabled:opacity-60"
                onClick={onSave}
                disabled={!onSave || saving || saved}
                title="Save these scenarios to the database so you can revisit them later."
              >
                {saved ? 'Saved' : saving ? 'Saving…' : 'Save scenarios'}
              </button>
            </div>
          )}

          {loading && (
            <div className="rounded-delphi border border-rule bg-paper p-4 text-center">
              <div className="text-sm text-muted">Generating scenarios...</div>
              <div className="mt-2 text-xs text-muted">
                This may take 30-60 seconds. Using GPT-5 Mini with medium reasoning.
              </div>
            </div>
          )}

          {error && (
            <div className="rounded-delphi border border-wine/30 bg-wine/10 p-3 text-sm text-wine">{error}</div>
          )}

          {data && !loading && (
            <div className="rounded-delphi border border-rule bg-paper p-4">
              <ScenariosDisplay scenarios={data.scenarios} />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
